<script lang="ts">
	//Stores
	import { getDeviceStatus, getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { devices } from 'src/stores/lists/devices';
	import { os } from 'src/stores/session/native-bridge';
	import { pin_unlocked_devices } from 'src/stores/device-runtime/pin-unlocked';
	//Services
	import { hapticFeedback } from '../../../services/native_container_requests_ios';
	import { format } from 'svelte-i18n';
	import { gen2Request } from '../../../services/gen_2_http_reqs';
	//Helpers
	import {
		get_gen_2_component_channel_structure,
		get_local_ip,
		real_id,
	} from '../../../helpers/interface_helpers';
	import { unlockPin } from '../../../helpers/pin_lock_helpers';
	import { hasControlRights } from 'src/helpers/user_helpers';
	//Design system components
	import {
		Button,
		ButtonGroup,
		CardGrid,
		ColorWheel,
		ControlsContainer,
		FavColor,
		Loader,
		Slider,
		SliderV2,
		SystemStyles,
	} from '@Shelly/shelly-design-system';
	import AwayModeActiveAlert from 'src/components/Alerts/AwayModeActiveAlert.svelte';
	//Components
	import PinUnlock from '../../PinUnlock.svelte';
	import { postReq } from 'src/services/http';
	import { beforeUpdate, onMount } from 'svelte';
	import { requestUserColors } from 'src/helpers/colors_helpers';
	import TransitionWrapper from 'src/components/Utility/TransitionWrapper.svelte';
	import ControlColorSelected from '../Group/ControlColorSelected.svelte';
	import { wait } from 'src/helpers/utilities';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';

	// Constants
	const DEBOUNCE_MS = 150;
	const COLOR_MIN = 0;
	const COLOR_MAX = 255;

	export let device, status, settings;

	const component = get_gen_2_component_channel_structure(device);

	$: hasRights = hasControlRights(device);

	// Check if away mode is active
	$: awayModeActive = status?.[component]?.away_mode || false;

	let showModal = false;
	let isLoading = false;
	let brightness = settings?.[component]?.ambient?.brightness || 0;
	let callbackValue: any; // Store expected brightness value to be forwarded if we have to pin-unlock

	$: settingsStore = getDeviceSettings(device.id);
	$: useVialColor = $settingsStore?.[component]?.ambient?.use_vial_color;
	let cacheUseVialColor = useVialColor;
	let loadingVialColor = false;

	let localIp = false;
	$: settingsStore = getDeviceSettings(device.id);
	$: colors = $settingsStore?.[component]?.ambient?.color;

	// Reactive brightness from store
	$: settingsStore = getDeviceSettings(device.id);
	$: storeBrightness = $settingsStore?.[component]?.ambient?.brightness;
	let isAdjustingBrightness = false;
	let cachedBrightness = typeof storeBrightness === 'number' ? storeBrightness : brightness;
	let isSettingBrightness = false;
	let brightnessAdjustTimeout: any;
	let displayedBrightness = cachedBrightness;

	// Pin slider to cached value while adjusting/setting, otherwise show store
	$: displayedBrightness =
		isAdjustingBrightness || isSettingBrightness
			? cachedBrightness
			: typeof storeBrightness === 'number'
				? storeBrightness
				: cachedBrightness;

	// Release pin only when store matches selected value
	$: if (isSettingBrightness && typeof storeBrightness === 'number') {
		if (storeBrightness === cachedBrightness) {
			isSettingBrightness = false;
		}
	}
	let value = colors ? { r: colors[0], g: colors[1], b: colors[2] } : { r: 255, g: 0, b: 0 };
	let isInteracting = false;
	let cachedColors = colors ? [...colors] : undefined;
	let cachedValue = value;
	let colorInteractionTimeout: any;

	$: if (colors && !loading && !isInteracting) {
		const colorsChanged =
			!cachedColors ||
			cachedColors[0] !== colors[0] ||
			cachedColors[1] !== colors[1] ||
			cachedColors[2] !== colors[2];
		if (colorsChanged) {
			value = { r: colors[0], g: colors[1], b: colors[2] };
			cachedColors = [...colors];
			cachedValue = value;
		}
	}
	let loading = false;
	let loadingFavorites = false;

	let favoriteColors = [];

	$: pinLocked =
		typeof $pin_unlocked_devices[device.id] === 'undefined' && $devices[device.id]?.pin_lock;

	const handlePin = () => {
		pinLocked = unlockPin(device, pinLocked);
		setBrightness({ detail: { value: callbackValue } });
		callbackValue = undefined;
	};

	const handleClose = () => {
		showModal = false;
		callbackValue = undefined;
	};

	const setBrightness = async (e) => {
		if (!pinLocked) {
			isLoading = true;
			isAdjustingBrightness = true;
			isSettingBrightness = true;
			let res;
			console.warn('e.detail.value', e.detail.value);
			// Optimistically update cache to prevent flicker
			cachedBrightness = e.detail.value;
			try {
				res = await gen2Request(device, 'cury.SetConfig', {
					id: device.channel,
					config: {
						ambient: {
							brightness: e.detail.value,
						},
					},
				});
				console.log('res', res);
				if (res?.isok) {
					cachedBrightness = e.detail.value;
				} else {
					throw new Error((res as any)?.error || 'Failed to set brightness');
				}
			} catch (err) {
				console.log(err);
			} finally {
				isLoading = false;
				isAdjustingBrightness = false;
				// Don't clear isSettingBrightness here - let the reactive guard do it
			}
			return res;
		} else {
			callbackValue = e.detail.value;
			showModal = true;
		}
	};

	const toggleColors = async (e) => {
		if (!pinLocked) {
			useVialColor = !useVialColor;
			const data = {
				config: {
					ambient: {
						use_vial_color: useVialColor,
					},
				},
			};
			try {
				const res = await gen2Request(device, 'cury.SetConfig', {
					id: device.channel,
					config: data.config,
				});
				if (res.isok) {
					const _settings = $settingsStore;
					_settings[component].ambient.use_vial_color = useVialColor;
					devices_settings.update_devices_settings(device.id, { ..._settings });
					cacheUseVialColor = useVialColor;
				} else {
					useVialColor = cacheUseVialColor;
					throw new Error((res as any)?.error || 'Failed to set color');
				}
			} catch (e) {
				useVialColor = cacheUseVialColor;
			}
		} else {
			callbackValue = e.detail.value;
			showModal = true;
		}
	};

	$: favColors1Mapped = favoriteColors.map((c) => {
		const currentColor = `rgb(${c.r}, ${c.g}, ${c.b})`;
		const isActive = `rgb(${value.r}, ${value.g}, ${value.b})` === currentColor;

		return {
			active: isActive,
			color: currentColor,
			colorObj: c,
		};
	});

	onMount(async () => {
		getValues();
		loadingFavorites = true;
		const favColorsReq = await requestUserColors();
		favoriteColors = favColorsReq ? favColorsReq : [];
		loadingFavorites = false;
	});

	beforeUpdate(() => {
		pinLocked =
			typeof $pin_unlocked_devices[device.id] === 'undefined' && $devices[device.id]?.pin_lock;
		if (JSON.stringify(value) !== JSON.stringify(cachedValue) && !loading) {
			//compare if value has changed by other user
			getValues();
			cachedValue = value;
		}

		// Update cache when store changes and not interacting
		if (
			!isLoading &&
			!isAdjustingBrightness &&
			!isSettingBrightness &&
			typeof storeBrightness === 'number'
		) {
			if (storeBrightness !== cachedBrightness) {
				cachedBrightness = storeBrightness;
			}
		}
	});

	// Initialize colors from store
	const getValues = () => {
		if (colors) {
			value = { r: colors[0], g: colors[1], b: colors[2] };
		}
	};

	// Utility functions
	const validateColor = (val: number) => Math.max(COLOR_MIN, Math.min(COLOR_MAX, +val || 0));
	const clearTimeoutSafely = (timeout: any) => timeout && clearTimeout(timeout);
	const hapticIfIOS = () => {
		if ($os === 'IOS') {
			hapticFeedback('buttonPressed');
		}
	};
	const hapticSelectionIfIOS = () => {
		if ($os === 'IOS') {
			hapticFeedback('selection');
		}
	};

	const handleSelectColor = async (selectedColor) => {
		loading = true;
		isInteracting = true;
		clearTimeoutSafely(colorInteractionTimeout);
		colorInteractionTimeout = setTimeout(() => {
			isInteracting = false;
		}, DEBOUNCE_MS);
		value = {
			r: validateColor(selectedColor.r),
			g: validateColor(selectedColor.g),
			b: validateColor(selectedColor.b),
		};
		try {
			if (!pinLocked) {
				hapticIfIOS();
				const colorData = {
					red: value.r,
					green: value.g,
					blue: value.b,
				};
				let res;

				cachedValue = {
					r: $settingsStore?.[component]?.ambient?.color[0],
					g: $settingsStore?.[component]?.ambient?.color[1],
					b: $settingsStore?.[component]?.ambient?.color[2],
				};

				res = await gen2Request(device, 'cury.SetConfig', {
					id: device.channel,
					config: {
						ambient: {
							color: [colorData.red, colorData.green, colorData.blue],
						},
					},
				});

				if (res.isok) {
					const _settings = $settingsStore;
					_settings[component].ambient.color = [
						colorData.red,
						colorData.green,
						colorData.blue,
					];
					devices_settings.update_devices_settings(device.id, { ..._settings });
					cachedValue = value;
					cachedColors = [colorData.red, colorData.green, colorData.blue];
				} else {
					value = cachedValue;
					throw new Error((res as any)?.error || 'Failed to set color');
				}
			} else {
				callbackValue = value;
				showModal = true;
			}
			loading = false;
			// let the debounce clear the interaction flag shortly after last change
		} catch (e) {
			console.error(e);
			loading = false;
			// let the debounce clear the interaction flag shortly after last change
		}
	};

	const onSaveColor = (newColor) => {
		favColors1Mapped = [
			...favColors1Mapped,
			{
				active: true,
				color: `rgb(${newColor.r}, ${newColor.g}, ${newColor.b})`,
				colorObj: newColor,
			},
		];
		updateColorsList();
	};

	const onDeleteColor = (index: number) => {
		favColors1Mapped = favColors1Mapped.filter((_, i) => i !== index);
		updateColorsList();
	};

	const updateColorsList = async () => {
		favoriteColors = favColors1Mapped.map((fc) => {
			return {
				...fc.colorObj,
			};
		});

		const data = {
			colors: favoriteColors,
		};
		try {
			const res = await postReq('/interface/user_colors/save', {
				data: JSON.stringify(data),
			});

			if (!res.isok) throw new Error('Failed to set colors');
		} catch (error) {
			console.error(error);
		}
	};

	const a11yProps = {
		sliderAriaLabel: $format('label_brightness', { default: 'Set brightness' }),
		ariaLabel: $format('label_brightness', { default: 'Set brightness' }),
		ariaLabelValue: $format('label_brightness', { default: 'Set brightness' }),
	};
</script>

{#if awayModeActive}
	<AwayModeActiveAlert {device} />
{/if}

<ButtonGroup
	id="buttongroup-d8z7aac"
	aria-label={$format('label_color_selection', { default: 'Color selection' })}
	class="mb-m"
	orientation="horizontal"
	noWrap
>
	<Button
		width="full"
		class={useVialColor ? 'active' : ''}
		size="m"
		on:click={toggleColors}
		disabled={awayModeActive || useVialColor || !hasRights}
		>{$format('label_use_vial_color', { default: 'Use vial color' })}</Button
	>
	<Button
		width="full"
		class={!useVialColor ? 'active' : ''}
		size="m"
		on:click={toggleColors}
		disabled={awayModeActive || !useVialColor || !hasRights}
		>{$format('label_use_custom_color', {
			default: 'Use custom color',
		})}</Button
	>
</ButtonGroup>

{#if $showNewLayout}
	<ControlsContainer class="mb-s" size="m" color={$showNewLayout ? 'color-0' : undefined}>
		<SliderV2
			id="slider-6ii3dmc"
			title={$format('label_brightness', { default: 'brightness' })}
			{a11yProps}
			stopSaveDeleteMarks={device.shared}
			value={displayedBrightness}
			type="incremented"
			class="d-flex flex-1 min-width-0"
			{isLoading}
			disabled={awayModeActive || useVialColor || !hasRights}
			slot="first"
			on:value_change={setBrightness}
			on:move={() => {
				isAdjustingBrightness = true;
				clearTimeoutSafely(brightnessAdjustTimeout);
				brightnessAdjustTimeout = setTimeout(() => {
					isAdjustingBrightness = false;
				}, DEBOUNCE_MS);
				hapticSelectionIfIOS();
			}}
		/>
	</ControlsContainer>
{:else}
	<div class="flex-full-height flex-direction-row mb-m">
		<Slider
			id="slider-6ii3dmc"
			title={$format('label_brightness', { default: 'brightness' })}
			{a11yProps}
			stopSaveDeleteMarks={device.shared}
			value={displayedBrightness}
			splitTitle
			type="incremented"
			class="d-flex flex-1 min-width-0"
			{isLoading}
			disabled={awayModeActive || useVialColor || !hasRights}
			on:value_change={setBrightness}
			on:move={() => {
				isAdjustingBrightness = true;
				clearTimeoutSafely(brightnessAdjustTimeout);
				brightnessAdjustTimeout = setTimeout(() => {
					isAdjustingBrightness = false;
				}, DEBOUNCE_MS);
				hapticSelectionIfIOS();
			}}
		/>
	</div>
{/if}
<ColorWheel
	id="colorwheel-0s2m33x"
	class="mb-s"
	a11yProps={{
		saveButtonAriaLabel: $format('label_save_color', { default: 'Save color' }),
		colorInputBAriaLabel: $format('label_color_input_b', { default: 'Color input B' }),
		colorInputGAriaLabel: $format('label_color_input_g', { default: 'Color input G' }),
		colorInputRAriaLabel: $format('label_color_input_r', { default: 'Color input R' }),
	}}
	valuesType={awayModeActive ? undefined : 'input'}
	valuesBackgroundColor={$showNewLayout ? SystemStyles.COLOR_0 : undefined}
	color={value}
	hideSaveBtn
	on:color_change={(e) => handleSelectColor(e.detail.color)}
	disabled={awayModeActive || loading || useVialColor || !hasRights}
	on:save_click={(event) => onSaveColor(event.detail.color)}
/>
<TransitionWrapper>
	{#if loadingFavorites}
		<Loader class="mb-m" />
	{:else if !useVialColor}
		{#if !awayModeActive}
			<ControlColorSelected
				class="mb-m"
				selectedColor={value}
				showSaveButton={!awayModeActive &&
					hasRights &&
					!favColors1Mapped.some(
						(fc) => JSON.stringify(fc.colorObj) === JSON.stringify(value),
					)}
				on:save_color={(event) => !awayModeActive && hasRights && onSaveColor(event.detail)}
			/>
			<CardGrid sm={90}>
				{#each favColors1Mapped as favColor, i}
					<FavColor
						a11yProps={{
							deleteButtonAriaLabel: $format('label_delete_color', {
								default: 'Delete color',
							}),
							favColorAriaLabel: $format('label_fav_color', { default: 'Favorite color' }),
						}}
						color={favColor.color}
						backgroundColor={$showNewLayout ? SystemStyles.COLOR_0 : undefined}
						on:click={() =>
							!awayModeActive &&
							!useVialColor &&
							hasRights &&
							handleSelectColor(favColor.colorObj)}
						on:delete={() =>
							!awayModeActive && !useVialColor && hasRights && onDeleteColor(i)}
						active={favColor.active}
					/>
				{/each}
			</CardGrid>
		{/if}
	{/if}
</TransitionWrapper>

{#if showModal}
	<PinUnlock
		bind:showModal
		on:unlock={handlePin}
		on:close={handleClose}
		on:cancel={() => (showModal = false)}
	/>
{/if}
